import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

export const CONTACT_CUSTOM_FIELDS_QUERY_KEY = 'contact/customFields';
export const useContactCustomFields = ({
	impersonationContext,
	refetchOnWindowFocus,
	enabled = true,
}: {
	impersonationContext?: Api.IImpersonationContext;
	refetchOnWindowFocus?: boolean;
	enabled?: boolean;
} = {}) => {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: async () => {
			return userSession.webServiceHelper.callAsync<Api.IFormResponse<string>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: CONTACT_CUSTOM_FIELDS_QUERY_KEY,
				}),
				'GET'
			);
		},
		queryKey: [CONTACT_CUSTOM_FIELDS_QUERY_KEY, impersonationContext?.account?.id ?? null],
		refetchOnWindowFocus,
	});
};

export const invalidateContactCustomFields = () => {
	reactQueryClient.invalidateQueries({ queryKey: [CONTACT_CUSTOM_FIELDS_QUERY_KEY] });
};

/**
 * Custom-field types eligible for the v1 contact filter search (exact-match equality).
 * Excludes: FileUpload, Note, MeetingScheduler, FullAddress, Custom, Name — none of which
 * work cleanly with a single equals-match value.
 */
const ELIGIBLE_CUSTOM_FIELD_FILTER_TYPES = new Set<Api.FormFieldType>([
	Api.FormFieldType.String,
	Api.FormFieldType.Text,
	Api.FormFieldType.Email,
	Api.FormFieldType.Option,
	Api.FormFieldType.MultipleOptions,
	Api.FormFieldType.Boolean,
	Api.FormFieldType.Integer,
	Api.FormFieldType.Decimal,
	Api.FormFieldType.Money,
	Api.FormFieldType.Date,
	Api.FormFieldType.DateTime,
	Api.FormFieldType.Time,
	Api.FormFieldType.User,
	Api.FormFieldType.Contact,
]);

export const filterEligibleCustomFields = (fields?: Api.IFormField<string>[]): Api.IFormField<string>[] => {
	return (fields ?? []).filter(
		f => !f.isHidden && ELIGIBLE_CUSTOM_FIELD_FILTER_TYPES.has(f.fieldType) && !!f.name && !!f.label?.trim()
	);
};

/**
 * Returns true if the account has at least one custom field eligible for v1 search
 * (visible, has a display label, supported fieldType). Use this to gate UI sections so
 * accounts without custom fields don't see an empty filter section.
 */
export const useHasEligibleContactCustomFields = (impersonationContext?: Api.IImpersonationContext): boolean => {
	const userSession = useUserSession();
	const hasCustomContactFields = userSession?.account?.additionalInfo?.hasCustomContactFields;
	const customFieldsQuery = useContactCustomFields({
		enabled: !!hasCustomContactFields,
		impersonationContext,
		refetchOnWindowFocus: false,
	});
	return filterEligibleCustomFields(customFieldsQuery.data?.fields).length > 0;
};

/**
 * Returns a `Map<fieldName, displayLabel>` for resolving the user-facing label of a
 * `CustomField` criterion's `values.FieldName`, plus the query's `isSuccess` flag so
 * callers can avoid showing the raw internal name during loading.
 */
export const useCustomFieldLabelMap = (
	impersonationContext?: Api.IImpersonationContext
): { labelByName: Map<string, string>; isSuccess: boolean } => {
	const userSession = useUserSession();
	const hasCustomContactFields = userSession?.account?.additionalInfo?.hasCustomContactFields;
	const customFieldsQuery = useContactCustomFields({
		enabled: !!hasCustomContactFields,
		impersonationContext,
		refetchOnWindowFocus: false,
	});
	const labelByName = useMemo(() => {
		const map = new Map<string, string>();
		(customFieldsQuery.data?.fields ?? []).forEach(f => {
			if (f.name) {
				map.set(f.name, f.label || f.name);
			}
		});
		return map;
	}, [customFieldsQuery.data?.fields]);
	return { labelByName, isSuccess: customFieldsQuery.isSuccess };
};
